import React, { useEffect, useRef } from "react"
import { Link as InertiaLink, router } from "@inertiajs/react"
import PropTypes from "prop-types"
import Button from "@kit/design/button"
import { canUseInertia } from "../navigation"

/**
 * Default prefetch time in inertia is 30 seconds. That might lead to unexpected behavior when data is cached for too long.
 * We only want to support the behavior where user hovers over a link and then navigates to it, 5 seconds seems to be enough for that.
 */
const DEFAULT_PREFETCH_CACHE_TIME = 5000

/**
 * Link component that conditionally renders either an Inertia Link or a regular anchor tag
 * based on the current route and feature flags.
 *
 * @param {React.ReactNode} children - Content to be rendered inside the link
 * @param {string} href - The URL the link points to
 * @param {boolean} [prefetch=false] - Whether to prefetch the link's destination
 * @param {Object} rest - Additional props to pass to the underlying link component
 * @returns {React.ReactElement} A Link component
 */
export const Link = ({ children, prefetch = false, ...props }) => {
  if (canUseInertia(props.href)) {
    return (
      <InertiaLink
        prefetch={prefetch}
        cacheFor={DEFAULT_PREFETCH_CACHE_TIME}
        {...props}
      >
        {children}
      </InertiaLink>
    )
  }

  const Component = props.as || "a"
  return <Component {...props}>{children}</Component>
}

Link.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string,
  prefetch: PropTypes.bool,
  as: PropTypes.elementType
}

/**
 * @deprecated Consider using Link component with as={Button} prop
 */
export const ButtonLink = ({
  children,
  prefetch = false,
  onClick = () => {},
  ...props
}) => {
  const hoverTimeout = useRef(null)

  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeout.current)
    }
  }, [])

  const handleClick = event => {
    event.preventDefault()
    onClick(event)
    router.visit(props.href, {
      preserveScroll: props.preserveScroll,
      preserveState: props.preserveState
    })
  }

  const prefetchHoverEvents = {
    onMouseEnter: () => {
      hoverTimeout.current = window.setTimeout(() => {
        router.prefetch(
          props.href,
          { method: "get" },
          { cacheFor: DEFAULT_PREFETCH_CACHE_TIME }
        ) // The last object will be destructured with default values inside Inertia code
      }, 75)
    },
    onMouseLeave: () => {
      clearTimeout(hoverTimeout.current)
    }
  }

  if (canUseInertia(props.href)) {
    return (
      <Button
        onClick={handleClick}
        {...(prefetch ? prefetchHoverEvents : {})}
        {...props}
      >
        {children}
      </Button>
    )
  }

  return (
    <Button {...props} onClick={onClick}>
      {children}
    </Button>
  )
}
